import React from "react";
import { Heart, ArrowRight, Landmark, ScrollText, ShieldCheck, Flag } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { PageHero, GoldBtn, OutlineBtn } from "@/components/Bits";
import { IMAGES, WHO_WE_ARE, HISTORY, TIMELINE, ORG } from "@/config/content";
import { useDonate } from "@/context/DonateContext";

const facts = [
  { Icon: Landmark, title: "501(c)(3) Nonprofit", body: `Legally registered in the ${ORG.state} as a not-for-profit organization.` },
  { Icon: ScrollText, title: "Tax-Deductible", body: "All donations to EHSIMFFA are fully tax-deductible." },
  { Icon: Flag, title: "Non-Political", body: "Not affiliated with any political movement, party or government." },
  { Icon: ShieldCheck, title: "A Single Purpose", body: "To fund scholarships for university students with demonstrated financial need." },
];

export default function About() {
  const { openDonate } = useDonate();
  return (
    <div>
      <Seo title="About the Foundation" description="Learn about EHSIMFFA — our mission, history, and relationship with the Emperor Haile Selassie I Memorial Foundation in Ethiopia." path="/about" />
      <PageHero
        kicker="About EHSIMFFA"
        title="A promise made across two continents."
        subtitle={WHO_WE_ARE.lead}
        image={IMAGES.emperorColor}
      />

      <section className="mx-auto max-w-5xl px-5 sm:px-8 py-20 sm:py-28 space-y-16">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-gold">Our Mission</h2>
          <p className="mt-5 text-lg leading-relaxed text-ivory2">{WHO_WE_ARE.mission}</p>
        </Reveal>
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-gold">Why We Exist</h2>
          <p className="mt-5 text-lg leading-relaxed text-ivory2">
            His Imperial Majesty believed that a nation's future is written in its classrooms. EHSIMFFA exists to
            carry that conviction forward — ensuring that promising students are not forced to abandon their
            education because of financial hardship.
          </p>
        </Reveal>
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-gold">Our History</h2>
          <p className="mt-5 text-lg leading-relaxed text-ivory2">{HISTORY.founding}</p>
          <p className="mt-4 text-lg leading-relaxed text-ivory2/85">{HISTORY.legal}</p>
        </Reveal>
      </section>

      {/* Timeline */}
      <section className="border-y border-gold/15 bg-ink2">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal><div className="kicker mb-4">Our Journey</div>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-ivory">Milestones of support.</h2></Reveal>
          <div className="mt-14 space-y-10">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.06}>
                <div className="grid gap-4 sm:grid-cols-[160px_1fr] border-t border-gold/20 pt-8">
                  <div className="font-serif text-3xl gold-text">{t.year}</div>
                  <div>
                    <h3 className="font-serif text-2xl text-ivory">{t.title}</h3>
                    <p className="mt-2 text-ivory2/85 leading-relaxed">{t.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Facts */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid gap-px bg-gold/15 sm:grid-cols-2 lg:grid-cols-4 hairline">
          {facts.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08} className="bg-ink p-8">
              <f.Icon className="h-8 w-8 text-gold" />
              <h3 className="mt-5 font-serif text-2xl text-ivory">{f.title}</h3>
              <p className="mt-2 text-ivory2/85 leading-relaxed">{f.body}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-14 flex flex-col sm:flex-row justify-center gap-4">
          <GoldBtn onClick={() => openDonate()} testid="about-donate"><Heart className="h-5 w-5" /> Donate Now</GoldBtn>
          <OutlineBtn to="/ethiopia" testid="about-work">Our Work in Ethiopia <ArrowRight className="h-4 w-4" /></OutlineBtn>
        </div>
      </section>
    </div>
  );
}
